export type TemplateType = 'booking_confirmation' | 'invoice' | 'itinerary' | 'payment_receipt' | 'voucher' | 'follow_up' | 'custom';
export type EmailStatus = 'Sent' | 'Delivered' | 'Opened' | 'Clicked' | 'Bounced' | 'Failed';

export interface EmailTemplate {
  id: string;
  name: string;
  type: TemplateType;
  subject: string;
  description: string;
  icon: string;
  color: string;
  bgColor: string;
  usageCount: number;
  lastUsed: string;
  tags: string[];
}

export interface SentEmail {
  id: string;
  to: string;
  toName: string;
  subject: string;
  templateType: TemplateType;
  templateName: string;
  sentAt: string;
  sentBy: string;
  status: EmailStatus;
  openedAt?: string;
  clickedAt?: string;
  bookingRef?: string;
  invoiceRef?: string;
  attachments: string[];
}

export const emailTemplates: EmailTemplate[] = [
  {
    id: 'tmpl-001',
    name: 'Booking Confirmation',
    type: 'booking_confirmation',
    subject: 'Your Booking is Confirmed! \u2014 {{booking_id}}',
    description: 'Professional confirmation email with booking summary, travel dates, passenger details, and hotel/flight information.',
    icon: 'ri-calendar-check-2-line',
    color: '#10b981',
    bgColor: '#ecfdf5',
    usageCount: 142,
    lastUsed: '2026-03-27',
    tags: ['Booking', 'Confirmation', 'Auto-send'],
  },
  {
    id: 'tmpl-002',
    name: 'Invoice & Payment Request',
    type: 'invoice',
    subject: 'Invoice {{invoice_number}} \u2014 {{agency_name}}',
    description: 'Branded invoice email with itemized services, total amount, payment instructions and bank details.',
    icon: 'ri-file-text-line',
    color: '#f59e0b',
    bgColor: '#fffbeb',
    usageCount: 98,
    lastUsed: '2026-03-26',
    tags: ['Invoice', 'Finance', 'Payment'],
  },
  {
    id: 'tmpl-003',
    name: 'Itinerary Share',
    type: 'itinerary',
    subject: 'Your {{destination}} Itinerary \u2014 {{travel_dates}}',
    description: 'Day-by-day itinerary email with destination highlights, included services and travel tips.',
    icon: 'ri-map-2-line',
    color: '#8b5cf6',
    bgColor: '#f5f3ff',
    usageCount: 76,
    lastUsed: '2026-03-25',
    tags: ['Itinerary', 'Travel Plan'],
  },
  {
    id: 'tmpl-004',
    name: 'Payment Receipt',
    type: 'payment_receipt',
    subject: 'Payment Received \u2014 {{amount}} ({{booking_id}})',
    description: 'Clean receipt email confirming payment amount, method, date and outstanding balance.',
    icon: 'ri-receipt-line',
    color: '#06b6d4',
    bgColor: '#ecfeff',
    usageCount: 115,
    lastUsed: '2026-03-27',
    tags: ['Payment', 'Receipt', 'Finance'],
  },
  {
    id: 'tmpl-005',
    name: 'Service Voucher',
    type: 'voucher',
    subject: 'Your Service Voucher \u2014 {{service_name}}',
    description: 'Printable service voucher email with hotel/tour/transfer details, confirmation codes and supplier info.',
    icon: 'ri-coupon-3-line',
    color: '#ec4899',
    bgColor: '#fdf2f8',
    usageCount: 54,
    lastUsed: '2026-03-24',
    tags: ['Voucher', 'Service'],
  },
  {
    id: 'tmpl-006',
    name: 'Pre-Departure Reminder',
    type: 'follow_up',
    subject: 'Your Trip to {{destination}} is in {{days_away}} Days!',
    description: 'Friendly pre-departure checklist email with packing tips, visa reminders, and important contact numbers.',
    icon: 'ri-flight-takeoff-line',
    color: '#f97316',
    bgColor: '#fff7ed',
    usageCount: 38,
    lastUsed: '2026-03-22',
    tags: ['Reminder', 'Pre-departure'],
  },
  {
    id: 'tmpl-007',
    name: 'Post-Trip Follow Up',
    type: 'follow_up',
    subject: 'How Was Your {{destination}} Trip? We\u2019d Love to Hear!',
    description: 'Warm post-trip email asking for feedback and offering loyalty rewards for the next booking.',
    icon: 'ri-star-smile-line',
    color: '#6366f1',
    bgColor: '#eef2ff',
    usageCount: 29,
    lastUsed: '2026-03-20',
    tags: ['Follow-up', 'Feedback', 'Loyalty'],
  },
  {
    id: 'tmpl-008',
    name: 'Custom Email',
    type: 'custom',
    subject: '',
    description: 'Start from a blank canvas and write a fully custom email to any customer.',
    icon: 'ri-edit-2-line',
    color: '#64748b',
    bgColor: '#f8fafc',
    usageCount: 22,
    lastUsed: '2026-03-19',
    tags: ['Custom', 'Blank'],
  },
];

export const sentEmails: SentEmail[] = [];

export const emailStats = {
  totalSent: 0,
  deliveryRate: 0,
  openRate: 0,
  clickRate: 0,
  bouncedCount: 0,
};
